import * as Sentry from '@sentry/react';
import { hasRecentRecoveryAttempt, isRecoverableAppLoadError } from '../utils/runtimeRecovery.js';
import { IS_STANDALONE } from '../config/runtimeProfile.js';

let initialized = false;

// Environments where Sentry init is skipped by default. The dev DSN shares its
// quota with stg/prod, so a runaway error loop in dev would otherwise burn the
// production error budget (BOU-1083). Opt back in for ad-hoc debugging with
// VITE_SENTRY_DEV_ENABLED=1.
const DEV_ENVIRONMENTS = new Set(['development', 'local']);

// Error messages to drop entirely — these are browser/infra noise, not app bugs.
// Only filter errors that are genuinely unactionable. Do NOT filter errors that
// could indicate real problems (e.g. API failures, React rendering bugs).
const IGNORED_ERRORS = [
  // Browser internals — nothing we can fix
  'ResizeObserver loop',
  'ResizeObserver loop completed with undelivered notifications',
  // Cancelled requests — user navigated away, expected
  'AbortError',
  'The operation was aborted',
  'The user aborted a request',
  // Browser extensions injecting errors — not our code
  'Extension context invalidated',
  // React DOM errors from browser extensions injecting/removing nodes in
  // React-managed subtrees. Not actionable — we can't control extensions.
  'removeChild',
  'insertBefore',
  // Socket.IO reconnection noise — handled by auto-reconnect with backoff
  'websocket error',
  'xhr poll error',
  'transport close',
];

function shouldDropEvent(event) {
  const message =
    event.exception?.values?.[0]?.value ||
    event.message ||
    '';

  // Drop recoverable app-load failures (stale chunks after a deploy) only when
  // the recovery flow hasn't yet attempted a reload. If a recent reload
  // already happened and we're STILL seeing the same error, reload didn't
  // recover the app — this is a real CDN/network outage and Sentry needs to
  // see it.
  if (isRecoverableAppLoadError(message) && !hasRecentRecoveryAttempt()) {
    return true;
  }

  return IGNORED_ERRORS.some((pattern) => message.includes(pattern));
}

function parseSampleRate(rawValue) {
  if (!rawValue) {
    return undefined;
  }

  const sampleRate = Number(rawValue);
  if (!Number.isFinite(sampleRate) || sampleRate < 0 || sampleRate > 1) {
    console.warn('[Sentry] Ignoring invalid sample rate:', rawValue);
    return undefined;
  }

  return sampleRate;
}

function isTruthy(rawValue) {
  if (rawValue === undefined || rawValue === null) {
    return false;
  }
  return ['1', 'true', 'yes', 'on'].includes(String(rawValue).trim().toLowerCase());
}

function resolveEnvironment() {
  return (
    import.meta.env.VITE_SENTRY_ENVIRONMENT ||
    import.meta.env.VITE_ENVIRONMENT ||
    import.meta.env.MODE ||
    'development'
  );
}

function normalizeError(error) {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string' && error.trim()) {
    return new Error(error);
  }

  if (error && typeof error === 'object' && typeof error.message === 'string') {
    return new Error(error.message);
  }

  return new Error('Unknown frontend error');
}

export function initSentry() {
  if (initialized) {
    return true;
  }

  // A Standalone Install makes no requests off the player's machine, whatever
  // DSN leaked into the build environment.
  if (IS_STANDALONE) {
    return false;
  }

  const dsn =import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) {
    console.warn('[Sentry] VITE_SENTRY_DSN is not set — frontend error reporting is disabled');
    return false;
  }

  const env = resolveEnvironment();
  const devOptIn = isTruthy(import.meta.env.VITE_SENTRY_DEV_ENABLED);
  if (DEV_ENVIRONMENTS.has(env) && !devOptIn) {
    console.info(
      `[Sentry] Disabled in environment="${env}" — set VITE_SENTRY_DEV_ENABLED=1 to opt in`
    );
    return false;
  }

  const tracesSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE);
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
  ) ?? 0.1;
  const replaysOnErrorSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
  ) ?? 1.0;

  const integrations = [
    Sentry.replayIntegration({
      // Don't reveal text/input values by default — flip with explicit allowlist
      // per-element via data-sentry-unmask when we want to see UI state.
      maskAllText: false,
      maskAllInputs: true,
      blockAllMedia: false,
    }),
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      showBranding: false,
      autoInject: false, // we'll mount the widget from a UI action instead of auto-injecting
    }),
    Sentry.httpClientIntegration(),
  ];

  if (typeof tracesSampleRate === 'number' && tracesSampleRate > 0) {
    integrations.push(Sentry.browserTracingIntegration());
  }

  const config = {
    dsn,
    environment: env,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: true,
    integrations,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    beforeSend(event) {
      return shouldDropEvent(event) ? null : event;
    },
  };

  if (typeof tracesSampleRate === 'number' && tracesSampleRate > 0) {
    config.tracesSampleRate = tracesSampleRate;
  }

  Sentry.init(config);
  Sentry.setTag('service', 'gaia-frontend');

  const branchTag = import.meta.env.VITE_GAIA_BRANCH_TAG;
  if (branchTag) {
    Sentry.setTag('branch', branchTag);
  }

  initialized = true;
  return true;
}

export function captureException(error, { tags = {}, extras = {} } = {}) {
  if (!initialized) {
    return;
  }

  const normalizedError = normalizeError(error);
  Sentry.withScope((scope) => {
    Object.entries(tags).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        scope.setTag(key, String(value));
      }
    });

    Object.entries(extras).forEach(([key, value]) => {
      if (value !== undefined) {
        scope.setExtra(key, value);
      }
    });

    Sentry.captureException(normalizedError);
  });
}

export function addBreadcrumb(breadcrumb) {
  if (!initialized) {
    return;
  }

  Sentry.addBreadcrumb(breadcrumb);
}

// BOU-1654 / BOU-2270: measurement helper for the audio-telemetry module.
//
// `Sentry.setMeasurement` attaches to the currently ACTIVE root span. The only
// root spans this app produces are `pageload` / `navigation` from
// browserTracingIntegration, and those have ended long before anything
// interesting happens mid-session — so for gameplay-time signals (inter-chunk
// gap, TTFA) it was a silent no-op on every call. Verified against prod:
// `p50(measurements.audio.inter_chunk_gap)` and `p50(measurements.audio.ttfa)`
// were both null over 30 days against count()=94, even though
// VITE_SENTRY_TRACES_SAMPLE_RATE=0.25 is set. Raising the sample rate would not
// have helped; there was simply nothing to attach to.
//
// So emit a STANDALONE span instead, whose duration IS the measured value. With
// no active parent it becomes its own root span and is sent under the existing
// tracesSampleRate, independent of whether a pageload transaction is still
// alive. Query it in Discover via `span.op:audio.measure`, and segment by the
// `seq` attribute (the segment 0->1 boundary is the one that matters for the
// narration-gap symptom).
//
// Still keeps the setMeasurement call: when a root span DOES happen to be live
// the value also lands on it, which preserves the pre-existing behaviour for
// anything measured during pageload.
export function recordMeasurement(name, value, unit = 'millisecond', attributes = {}) {
  if (!initialized) {
    return;
  }

  Sentry.setMeasurement(name, value, unit);

  if (!Number.isFinite(value) || value < 0) {
    return;
  }

  // Sentry takes span times as seconds-since-epoch. Reconstruct the interval the
  // measurement describes so the span's duration carries the value even for
  // consumers that only read duration.
  const endSeconds = Date.now() / 1000;
  const startSeconds = endSeconds - value / 1000;

  const span = Sentry.startInactiveSpan({
    name,
    op: 'audio.measure',
    startTime: startSeconds,
    onlyIfParent: false,
    attributes: {
      'audio.measurement': name,
      'audio.value_ms': value,
      'audio.unit': unit,
      ...attributes,
    },
  });
  span?.end(endSeconds);
}

export function setUser(user) {
  if (!initialized) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  // Auth0 user has `sub`, `email`, `name`. Use sub as the Sentry id when present.
  Sentry.setUser({
    id: user.sub || user.id || undefined,
    email: user.email || undefined,
    username: user.name || user.username || undefined,
  });
}

export function showFeedbackDialog(options = {}) {
  if (!initialized) {
    return null;
  }
  const feedback = Sentry.getFeedback();
  if (!feedback) {
    return null;
  }
  return feedback.openDialog(options);
}
